import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { SmsWalletApis } from "../../apis/smsApis";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Flutterwave returns the user here after checkout. The wallet is credited by
// the /api/webhook/flutterwave webhook, NOT by this page - the redirect is not
// reliable (users close the tab, networks drop). So this page only watches for
// the webhook to land and reports what it sees.
const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 20000;

// Flutterwave has used both "successful" and "completed" for a good payment.
// Treat anything that isn't an explicit cancel/fail as "probably paid".
const FAILED_STATUSES = ["cancelled", "canceled", "failed"];

type Phase = "confirming" | "credited" | "pending" | "failed";

export default function VerifyPayment() {
  const navigate = useNavigate();
  const location = useLocation();
  const [phase, setPhase] = useState<Phase>("confirming");
  const cancelled = useRef(false);

  useEffect(() => {
    cancelled.current = false;

    const query = new URLSearchParams(location.search);
    const txRef = query.get("tx_ref");
    const status = (query.get("status") || "").toLowerCase();

    if (!txRef) {
      navigate("/sms-wallet");
      return;
    }

    if (FAILED_STATUSES.includes(status)) {
      setPhase("failed");
      return;
    }

    watchForCredit(txRef);

    return () => { cancelled.current = true; };
  }, [location]);

  // Poll the transaction list until the webhook flips this reference to
  // completed. We never write anything here - the webhook owns the credit.
  const watchForCredit = async (txRef: string) => {
    const deadline = Date.now() + POLL_TIMEOUT_MS;

    while (!cancelled.current && Date.now() < deadline) {
      try {
        const res = await SmsWalletApis.getTransactions(15);
        const rows: any[] = res?.data?.data?.data ?? [];
        const match = rows.find((t) => t?.reference === txRef);

        if (match?.status === "completed") {
          if (!cancelled.current) {
            setPhase("credited");
            toast.success("Payment confirmed. Your wallet has been credited.");
          }
          return;
        }

        if (match?.status === "failed") {
          if (!cancelled.current) setPhase("failed");
          return;
        }
      } catch {
        // Transient error - keep polling until the deadline.
      }

      await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
    }

    // Webhook hasn't arrived yet. It usually still will, so don't alarm the
    // customer - their money is not lost, the credit is just in flight.
    if (!cancelled.current) setPhase("pending");
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      <div className="max-w-md w-full bg-white rounded-lg shadow-xl p-8">
        {phase === "confirming" ? (
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mb-4"></div>
            <h2 className="text-xl font-semibold text-gray-800 mb-2">Confirming Your Payment</h2>
            <p className="text-gray-600">This usually takes a few seconds. Please don't close this page.</p>
          </div>
        ) : phase === "credited" ? (
          <div className="text-center">
            <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-green-100 text-green-600 mb-4">
              <svg className="h-10 w-10" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-800 mb-2">Payment Successful!</h2>
            <p className="text-gray-600 mb-6">Your SMS wallet has been credited successfully.</p>
            <button
              onClick={() => navigate("/sms-wallet")}
              className="bg-[#0071BC] hover:bg-blue-700 text-white font-medium py-2 px-6 rounded-lg"
            >
              Go to SMS Wallet
            </button>
          </div>
        ) : phase === "pending" ? (
          <div className="text-center">
            <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-blue-100 text-blue-600 mb-4">
              <svg className="h-10 w-10" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-800 mb-2">Payment Received</h2>
            <p className="text-gray-600 mb-6">
              We've received your payment and your wallet will be credited shortly.
              You can safely close this page - no need to pay again.
            </p>
            <button
              onClick={() => navigate("/sms-wallet")}
              className="bg-[#0071BC] hover:bg-blue-700 text-white font-medium py-2 px-6 rounded-lg"
            >
              Go to SMS Wallet
            </button>
          </div>
        ) : (
          <div className="text-center">
            <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-red-100 text-red-600 mb-4">
              <svg className="h-10 w-10" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-800 mb-2">Payment Not Completed</h2>
            <p className="text-gray-600 mb-6">Your payment was cancelled or declined, and you have not been charged.</p>
            <div className="flex flex-col space-y-2">
              <button
                onClick={() => navigate("/sms-wallet")}
                className="bg-[#0071BC] hover:bg-blue-700 text-white font-medium py-2 px-6 rounded-lg"
              >
                Return to SMS Wallet
              </button>
              <button
                onClick={() => navigate("/support")}
                className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium py-2 px-6 rounded-lg"
              >
                Contact Support
              </button>
            </div>
          </div>
        )}
      </div>
      <ToastContainer position="top-right" autoClose={5000} />
    </div>
  );
}
